import { TokensStore } from '@streamelements/auth-tools';
import { AuthContextProvider, ServiceEnvironment } from '@streamelements/client-sdk-js';
import { ScopedStorageItemInfo } from '../types/obs';
import { Analytics } from '../services/AnalyticsService';
import { ensureOBSPresence } from './ensureOBSPresence';
import { promisefy } from './promisefy';

function add30Days() {
  return new Date(new Date().getTime() + 1000 * 60 * 60 * 24 * 30);
}

export function createAuthTokenCookieOptions() {
  return {
    domain: window.location.hostname === 'localhost' ? 'localhost' : '.streamelements.com',
    expires: add30Days(),
  };
}

function getToken() {
  const tokensStore = new TokensStore();
  return tokensStore.creator.get();
}

export function setToken(name: 'creator', value: string) {
  const tokensStore = new TokensStore(createAuthTokenCookieOptions());

  if (!value) {
    tokensStore[name].clear();
    return;
  }

  tokensStore[name].set(value, createAuthTokenCookieOptions());
}

export function createAuthContextProvider(serviceEnvironment: ServiceEnvironment) {
  const token = getToken();

  console.log({ serviceEnvironment });

  const anonymousAuthContextProvider = new AuthContextProvider({
    serviceEnvironment,
    allowAnonymous: true,
    tokenType: 'anonymous',
  });

  if (!token) {
    return anonymousAuthContextProvider;
  }

  try {
    /**
     * Shared channel is set as `executeJavascriptOnload` when creating docks or modal dialogs.
     * This essentially "preloads" the channel and makes it available to the auth context provider.
     */
    const sharedChannel = window.__streamelements_channel;

    /**
     * Auth context provider will throw if we don't provide a token and there isn't one in cookies.
     */
    const authContextProvider = new AuthContextProvider({
      serviceEnvironment,
      tokenType: 'Bearer',
      token,
      isImpersonated: false,
      sharedChannel,
      sharedProfile: {} as never, // Suppress CRM profile loading
    });

    (async () => {
      await ensureOBSPresence(false);
      const { hostMachineUniqueId } = await promisefy(window.host.getHostProperties)();

      // TODO: Set identity to channel id after we get clearance on legality
      Analytics.setIdentity({ identity: 'anonymous' });
      Analytics.addContextParams({ hostMachineID: hostMachineUniqueId });
    })();

    return authContextProvider;
  } catch (error) {
    return anonymousAuthContextProvider;
  }
}

export function getConnectPlatformScopedStorageItemId() {
  return {
    container: 'streamelements_multi-streaming',
    scope: 'connect',
    item: 'authorization_token',
  } satisfies ScopedStorageItemInfo;
}

export class UnauthenticatedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthenticatedError';
  }
}

export class BadRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'BadRequestError';
  }
}
